import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DefaultLocale } from '@app/+authenticated/shared/locale/locale.helper';
import { ContactSales } from '@app/helpers/contact-sales.helper';
import { getMarketingUrl } from '@app/helpers/marketing-url/marketing-url.helper';
import { selectAccountCountryCode } from '@app/reducers/account/account.selector';
import { selectPermissionCanManageAccount } from '@app/reducers/orm/permission/permission.selectors';
import { AddonOfferApiService } from '@app/services/addon-offer/addon-offer-api.service';
import { hrProFreeMonthOfferKey } from '@app/services/addon-offer/addon-offer.model';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AppState } from '@reducers/interfaces';
import { ApiGateway } from '@sb/shared/api';
import { featureFlag, FeatureService } from '@sb/shared/feature';
import { DialogAnnouncementData, SbDialogService } from '@sb/ui/dialog';
import { catchError, finalize, map, of, take, timeout } from 'rxjs';

export const hrProFreeMonthOfferRoute = ['/offers', hrProFreeMonthOfferKey];
// Past this the locked item would feel dead; sales is the safe answer when the backend is slow.
export const hrProEligibilityTimeoutMs = 3000;
export const hrProAddonRequestEndpoint = 'addon-requests/hr-pro';

type HrProUpsellVariant = 'startFreeTrial' | 'getInTouch' | 'askYourAdmin';

@Injectable({ providedIn: 'root' })
export class HrProUpsellDialogService {
  #store = inject<Store<AppState>>(Store);
  #translate = inject(TranslateService);
  #router = inject(Router);
  #dialogService = inject(SbDialogService);
  #document = inject(DOCUMENT);
  #gateway = inject(ApiGateway);
  #featureService = inject(FeatureService);
  #offerApi = inject(AddonOfferApiService);

  #canManageAccount = this.#store.selectSignal(selectPermissionCanManageAccount);
  #countryCode = this.#store.selectSignal(selectAccountCountryCode);
  #checkingEligibility = false;

  open(): void {
    if (!this.#canManageAccount()) {
      this.#openAs('askYourAdmin');

      return;
    }

    if (!this.#featureService.isEnabled(featureFlag.TMP_ADDON_OFFER_CLAIM)) {
      this.#openAs('getInTouch');

      return;
    }

    if (this.#checkingEligibility) {
      return;
    }

    this.#checkingEligibility = true;

    // The backend owns the offer's eligibility rules (billing period, trial, beta history, prior claim);
    // when it cannot answer, sales is the only safe door.
    this.#offerApi
      .eligibility(hrProFreeMonthOfferKey)
      .pipe(
        take(1),
        timeout(hrProEligibilityTimeoutMs),
        map((eligibility): HrProUpsellVariant => (eligibility.claimable ? 'startFreeTrial' : 'getInTouch')),
        catchError(() => of<HrProUpsellVariant>('getInTouch')),
        finalize(() => (this.#checkingEligibility = false)),
      )
      .subscribe((variant) => this.#openAs(variant));
  }

  #openAs(variant: HrProUpsellVariant): void {
    this.#dialogService.openAnnouncement({
      title: this.#translate.instant('Hire and onboard without the paperwork'),
      description: this.#description(variant),
      image: {
        src: 'assets/images/announcement/hr-pro.svg',
      },
      primary: this.#primaryAction(variant),
      secondary: this.#learnMoreLink(),
    });
  }

  #description(variant: HrProUpsellVariant): string {
    const whatItDoes = this.#translate.instant(
      'HR Pro collects contract details from new hires, generates and signs the contract, and handles renewals so nothing lapses. All in Shiftbase, no chasing documents over email.',
    );

    return `${whatItDoes}\n${this.#nextStep(variant)}`;
  }

  #nextStep(variant: HrProUpsellVariant): string {
    switch (variant) {
      case 'askYourAdmin':
        return this.#translate.instant('Only admins can turn on add-ons. Ask yours and we’ll send them the details.');
      case 'getInTouch':
        return this.#translate.instant(
          'Get in touch and we’ll add HR Pro to your plan. Your first 30 days are free, then it’s €1 per employee a month until 1 January 2027.',
        );
      case 'startFreeTrial':
        return this.#translate.instant(
          'Your first 30 days are free. After that it’s €1 per employee a month until 1 January 2027.',
        );
    }
  }

  #primaryAction(variant: HrProUpsellVariant): DialogAnnouncementData['primary'] {
    switch (variant) {
      case 'askYourAdmin':
        return this.#askYourAdminAction();
      case 'getInTouch':
        return this.#getInTouchAction();
      case 'startFreeTrial':
        return this.#startFreeTrialAction();
    }
  }

  // The owner is emailed by the backend; success and failure toasts come from the response
  // meta message through the interceptors, like the Compliance+ request.
  #askYourAdminAction(): DialogAnnouncementData['primary'] {
    return {
      text: this.#translate.instant('Ask your admin'),
      onClick: () => {
        this.#gateway.post(hrProAddonRequestEndpoint, {}).pipe(take(1)).subscribe();
      },
    };
  }

  #startFreeTrialAction(): DialogAnnouncementData['primary'] {
    return {
      text: this.#translate.instant('Start free trial'),
      onClick: () => {
        this.#router.navigate(hrProFreeMonthOfferRoute);
      },
    };
  }

  #getInTouchAction(): DialogAnnouncementData['primary'] {
    return {
      text: this.#translate.instant('Get in touch'),
      onClick: () => {
        this.#document.location.assign(this.#salesMailto());
      },
    };
  }

  #salesMailto(): string {
    const salesEmail = new ContactSales(this.#countryCode()).enterpriseEmail;
    const subject = encodeURIComponent(this.#translate.instant('Add HR Pro to our plan'));

    return `mailto:${salesEmail}?subject=${subject}`;
  }

  #learnMoreLink(): DialogAnnouncementData['secondary'] {
    const href = getMarketingUrl('hrPro', this.#translate.getCurrentLang() ?? DefaultLocale);

    if (!href) {
      return undefined;
    }

    return {
      text: this.#translate.instant('Learn more'),
      href,
    };
  }
}
